import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, MoreHorizontal, Pencil, Plus, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  Textarea,
  toast,
} from "@lumanity/ui/primitives";
import PromptPicker from "@/features/draft/PromptPicker";
import { useAdminTemplates, useOutputTypes, useTemplateMutations } from "../outputsApi";
import { OUTPUT_TYPE_LABELS, moveItem, sectionsFromOutline, templateBody } from "../model";
import type { OutlineTemplate, OutlineTemplateInput, OutputType, TemplateSection } from "../types";

const emptySection = (): TemplateSection => ({
  id: null,
  title: "",
  word_limit: null,
  instructions: "",
  subsections: [],
});

const toInput = (t: OutlineTemplate): OutlineTemplateInput => ({
  name: t.name,
  description: t.description,
  output_type: t.output_type,
  is_active: t.is_active,
  sections: t.sections,
});

interface Editing {
  id: string | null;
  value: OutlineTemplateInput;
  /** A new template whose sections are still the type's current outline: changing the type swaps them. */
  pristine: boolean;
}

// The Add/Edit Template pop-up is laid out like heor.pico.frontend's TemplateForm
// (Admin → Templates): template details, then read-only section cards each with a "⋯"
// menu, a small Edit section pop-up, a dashed add button and full-width Cancel/Save.

// As the library's MasterDataTable pages its rows (its paginate helper isn't exported).
const PAGE_SIZE = 10;

const popoverItemClass = "w-full text-left text-sm px-3 py-1.5 rounded transition-colors flex items-center gap-2";

const SectionFormDialog = ({
  section,
  isNew,
  onCancel,
  onSave,
}: {
  section: TemplateSection | null;
  isNew: boolean;
  onCancel: () => void;
  onSave: (section: TemplateSection) => void;
}) => {
  const [value, setValue] = useState<TemplateSection>(section ?? emptySection());
  const [subsections, setSubsections] = useState("");

  useEffect(() => {
    if (section) {
      setValue(section);
      setSubsections(section.subsections.join("\n"));
    }
  }, [section]);

  const save = () =>
    onSave({
      ...value,
      title: value.title.trim(),
      subsections: subsections.split("\n"),
    });

  return (
    <Dialog open={!!section} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add section" : "Edit section"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label htmlFor="section-title" className="text-sm font-medium">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="section-title"
              value={value.title}
              onChange={(e) => setValue({ ...value, title: e.target.value })}
              placeholder="Section title"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="section-limit" className="text-sm font-medium">
              Word limit
            </label>
            <Input
              id="section-limit"
              type="number"
              min={1}
              placeholder="No limit"
              value={value.word_limit ?? ""}
              onChange={(e) =>
                setValue({
                  ...value,
                  word_limit: e.target.value ? Math.max(1, Number(e.target.value)) : null,
                })
              }
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="section-instructions" className="text-sm font-medium">
              AI instructions
            </label>
            <div className="flex gap-2">
              <Textarea
                id="section-instructions"
                value={value.instructions}
                onChange={(e) => setValue({ ...value, instructions: e.target.value })}
                placeholder="Write the AI instructions, or choose from the library..."
                className="min-h-[80px]"
              />
              {/* Copies the prompt's text: the template keeps its own copy. */}
              <PromptPicker disabled={false} onPick={(prompt) => setValue({ ...value, instructions: prompt.text })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="section-subsections" className="text-sm font-medium">
              Subsections
            </label>
            <Textarea
              id="section-subsections"
              value={subsections}
              onChange={(e) => setSubsections(e.target.value)}
              placeholder="One subsection title per line"
              className="min-h-[80px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" className="bg-brand-teal text-white hover:bg-brand-teal/90" onClick={save} disabled={!value.title.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateFormDialog = ({
  editing,
  saving,
  error,
  canSave,
  onClose,
  onSave,
  setValue,
  setType,
  setSections,
}: {
  editing: Editing | null;
  saving: boolean;
  error: string | null;
  canSave: boolean;
  onClose: () => void;
  onSave: () => void;
  setValue: (patch: Partial<OutlineTemplateInput>) => void;
  setType: (type: OutputType) => void;
  setSections: (sections: TemplateSection[]) => void;
}) => {
  const [menuIndex, setMenuIndex] = useState<number | null>(null);
  // index null: a new section, appended on save.
  const [sectionEdit, setSectionEdit] = useState<{
    index: number | null;
    section: TemplateSection;
  } | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const sections = editing?.value.sections ?? [];

  const saveSection = (section: TemplateSection) => {
    if (!sectionEdit) return;
    const index = sectionEdit.index;
    setSections(index === null ? [...sections, section] : sections.map((s, i) => (i === index ? section : s)));
    setSectionEdit(null);
  };

  const move = (from: number, to: number) => {
    setMenuIndex(null);
    setSections(moveItem(sections, from, to));
  };

  return (
    <>
      <Dialog open={!!editing} onOpenChange={(open) => !open && !saving && onClose()}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Template" : "Create Template"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form
              className="mt-6 space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSave && !saving) onSave();
              }}
            >
              {/* Template metadata */}
              <div className="space-y-4 pb-6 border-b">
                <div className="space-y-1.5">
                  <label htmlFor="template-name" className="text-sm font-medium">
                    Template name
                  </label>
                  <Input
                    id="template-name"
                    value={editing.value.name}
                    onChange={(e) => setValue({ name: e.target.value })}
                    placeholder="e.g. Congress abstract"
                    disabled={saving}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="template-description" className="text-sm font-medium">
                    Description
                  </label>
                  <Textarea
                    id="template-description"
                    value={editing.value.description ?? ""}
                    onChange={(e) => setValue({ description: e.target.value })}
                    placeholder="Describe this template..."
                    className="min-h-[60px]"
                    disabled={saving}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="template-active"
                    checked={editing.value.is_active}
                    onCheckedChange={(c) => setValue({ is_active: c })}
                    disabled={saving}
                  />
                  <Label htmlFor="template-active" className="cursor-pointer text-sm font-medium">
                    Active
                  </Label>
                  <span className="text-xs text-muted-foreground">(used for new First drafts of this type)</span>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="template-type" className="text-sm font-medium">
                    Output Type <span className="text-destructive">*</span>
                  </label>
                  <Select value={editing.value.output_type} onValueChange={(v) => setType(v as OutputType)} disabled={saving}>
                    <SelectTrigger id="template-type">
                      <SelectValue placeholder="Select output type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(OUTPUT_TYPE_LABELS) as OutputType[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {OUTPUT_TYPE_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sections list */}
              <h3 className="font-semibold">Sections</h3>

              {sections.map((section, index) => (
                // Index keys: cards are positions in the outline.
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-block p-1 text-center bg-gray-100 text-xs text-muted-foreground w-6 shrink-0">{index + 1}</span>
                      <span className="text-sm font-medium truncate">{section.title || "Untitled"}</span>
                      {section.word_limit && <span className="text-xs text-muted-foreground shrink-0">· {section.word_limit} words</span>}
                    </div>
                    <Popover open={menuIndex === index} onOpenChange={(open) => setMenuIndex(open ? index : null)}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-brand-teal transition-colors ml-2 p-1 rounded"
                          aria-label={`${section.title || "Section"} options`}
                          disabled={saving}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-36 p-1" align="end">
                        <button
                          type="button"
                          className={`${popoverItemClass} hover:bg-accent`}
                          onClick={() => {
                            setMenuIndex(null);
                            setSectionEdit({ index, section });
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        {index > 0 && (
                          <button type="button" className={`${popoverItemClass} hover:bg-accent`} onClick={() => move(index, index - 1)}>
                            <ArrowUp className="w-3.5 h-3.5" /> Move up
                          </button>
                        )}
                        {index < sections.length - 1 && (
                          <button type="button" className={`${popoverItemClass} hover:bg-accent`} onClick={() => move(index, index + 1)}>
                            <ArrowDown className="w-3.5 h-3.5" /> Move down
                          </button>
                        )}
                        {sections.length > 1 && (
                          <button
                            type="button"
                            className={`${popoverItemClass} text-destructive hover:bg-destructive/10`}
                            onClick={() => {
                              setMenuIndex(null);
                              setDeleteIndex(index);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Textarea
                    value={section.instructions}
                    placeholder="No AI instructions"
                    className="min-h-[60px] focus-visible:ring-0 focus-visible:ring-offset-0 cursor-default"
                    readOnly
                    aria-label={`${section.title || "Section"} AI instructions`}
                  />
                  {section.subsections.some((s) => s.trim()) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {section.subsections
                        .filter((s) => s.trim())
                        .map((sub, i) => (
                          <Badge key={i} variant="secondary">
                            {sub}
                          </Badge>
                        ))}
                    </div>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed"
                disabled={saving}
                onClick={() => setSectionEdit({ index: null, section: emptySection() })}
              >
                + Add section
              </Button>

              {error && <ErrorBanner message={error} />}

              {/* Form actions */}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-brand-teal text-white hover:bg-brand-teal/90" disabled={saving || !canSave}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <SectionFormDialog
        section={sectionEdit?.section ?? null}
        isNew={sectionEdit?.index === null}
        onCancel={() => setSectionEdit(null)}
        onSave={saveSection}
      />

      <AlertDialog open={deleteIndex !== null} onOpenChange={(open) => !open && setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete section</AlertDialogTitle>
            <AlertDialogDescription>
              Remove “{deleteIndex !== null ? sections[deleteIndex]?.title || "Untitled" : ""}” from this template? Outputs already drafted keep their
              own outline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteIndex !== null) setSections(sections.filter((_, i) => i !== deleteIndex));
                setDeleteIndex(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Outline templates (stored in the prompt library, as in PICO): the sections a new
 * First draft of each output type starts from. Starting a draft copies the active
 * template, so edits here never change existing outputs.
 */
const OutlineTemplatesAdmin = () => {
  const { data: templates = [], isLoading, error } = useAdminTemplates();
  const { data: types = [] } = useOutputTypes();
  const { create, update, remove } = useTemplateMutations();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<OutlineTemplate | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(templates.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = templates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const outlineOf = (type: OutputType) => sectionsFromOutline(types.find((t) => t.type === type)?.outline ?? []);

  const openEditor = (template: OutlineTemplate | null) => {
    setDialogError(null);
    if (template) {
      setEditing({
        id: template.id,
        value: toInput(template),
        pristine: false,
      });
      return;
    }
    // No active template for a type yet: start inactive, from the type's current outline.
    setEditing({
      id: null,
      pristine: true,
      value: {
        name: "",
        description: null,
        output_type: "abstract",
        is_active: false,
        sections: outlineOf("abstract"),
      },
    });
  };

  const setValue = (patch: Partial<OutlineTemplateInput>, stillPristine = false) =>
    setEditing((e) =>
      e
        ? {
            ...e,
            pristine: e.pristine && stillPristine,
            value: { ...e.value, ...patch },
          }
        : e,
    );

  const setType = (output_type: OutputType) =>
    editing?.pristine ? setValue({ output_type, sections: outlineOf(output_type) }, true) : setValue({ output_type });

  const setSections = (sections: TemplateSection[]) => setValue({ sections });

  const saving = create.isPending || update.isPending;
  const sections = editing?.value.sections ?? [];
  const canSave = !!editing?.value.name.trim() && sections.length > 0 && sections.every((s) => s.title.trim());

  const handleSave = async () => {
    if (!editing) return;
    const body = templateBody(editing.value);
    try {
      if (editing.id) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      toast({ title: editing.id ? "Template updated" : "Template added" });
      setEditing(null);
    } catch (e) {
      setDialogError((e as Error).message);
    }
  };

  // Activating a second template for a type is refused (409); the toast says which is active.
  const toggleActive = (template: OutlineTemplate) =>
    update.mutate(
      {
        id: template.id,
        body: templateBody({
          ...toInput(template),
          is_active: !template.is_active,
        }),
      },
      {
        onError: (e) =>
          toast({
            title: "Could not update the template",
            description: e.message,
            variant: "destructive",
          }),
      },
    );

  const handleDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => toast({ title: `${deleting.name} deleted` }),
      onError: (e) =>
        toast({
          title: "Could not delete the template",
          description: e.message,
          variant: "destructive",
        }),
      onSettled: () => setDeleting(null),
    });
  };

  if (error) return <ErrorBanner message={`Could not load templates: ${error.message}`} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          One template per output type can be active. A type needs an active template before its outputs can start a First draft.
        </p>
        <Button size="sm" onClick={() => openEditor(null)} className="gap-1 bg-brand-teal text-white hover:bg-brand-teal/90">
          <Plus className="h-4 w-4" /> Add template
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Output type</TableHead>
              <TableHead>Sections</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  Loading templates...
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  No templates yet. Add one per output type so outputs can start a First draft.
                </TableCell>
              </TableRow>
            ) : (
              pageItems.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>{OUTPUT_TYPE_LABELS[t.output_type]}</TableCell>
                  <TableCell className="text-muted-foreground">{t.sections.map((s) => s.title).join(", ")}</TableCell>
                  <TableCell>
                    <Badge variant={t.is_active ? "default" : "secondary"}>{t.is_active ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => openEditor(t)} aria-label={`Edit ${t.name}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t.is_active ? "Deactivate" : "Activate"}
                        aria-label={`${t.is_active ? "Deactivate" : "Activate"} ${t.name}`}
                        onClick={() => toggleActive(t)}
                        disabled={update.isPending}
                      >
                        {t.is_active ? <ToggleRight className="h-4 w-4 text-brand-teal" /> : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                      <Button size="icon" variant="ghost" title="Delete" onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />

      <TemplateFormDialog
        editing={editing}
        saving={saving}
        error={dialogError}
        canSave={canSave}
        onClose={() => setEditing(null)}
        onSave={() => void handleSave()}
        setValue={setValue}
        setType={setType}
        setSections={setSections}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Outputs already drafted from it keep their outline. To stop using it without deleting, mark it inactive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutlineTemplatesAdmin;
